/* ============================================================================
   עגלת הצד (drawer / canvas) — התאמה למבנה של עמוד העגלה
   נוצר 31.7.2026.

   ── הרקע ──
   עגלת הצד היא תבנית *נפרדת לחלוטין* מעמוד העגלה:
       snippets/header-cart-content.liquid   →  מוזרק לתוך #bt-sidebar-cart
   היא משתמשת במחלקות אחרות לגמרי מאלה של עמוד העגלה:
       שורת מוצר   .hc__item        (ולא .cart__row)
       בורר כמות   .qty-box         (ולא .qty-box--cart)
       סיכום       .hc__total       (ולא .pg-cart-summary-col)
   ולכן *אף אחד* מהתיקונים שנעשו לעמוד העגלה (ב-pg-slider.css וב-pg-cart-extras.js)
   לא הגיע אליה — היא נשארה במבנה המקורי של התמה. זה בדיוק מה שנראה בפועל.

   ── מה נמדד בפועל (drawer חי, 31.7.2026) ──
   1. בורר הכמות: .qty-box__ctrl עם position:absolute, left:-30px, top:20px,
      ו-border: 1px solid — כלומר שני הכפתורים מוערמים זה על זה *מחוץ* לקופסה
      משמאל, בדיוק אותה תקלה שתוקנה בעמוד העגלה ב-26.7.
   2. הסיכום: שורה אחת בלבד — "סיכום 2,297 ₪" — בלי ספירת פריטים, בלי מחיר
      מלא מחוק ובלי שורת הנחה, גם כשבעגלה יש הנחה בפועל של 399.50 ₪.
      כלומר הלקוח לא רואה בכלל שהוא חוסך.
   3. שורת המחיר: "x 699 ₪" במשקל bold — הפורמט הישן.

   ── מה הקובץ הזה עושה ──
   • CSS: מיישר את .qty-box של ה-drawer לאותה פריסת flex כמו .qty-box--cart
     (מינוס | מספר | פלוס, בלי מסגרות), ומסיר את ה-bold וה-"x" משורת המחיר.
   • JS: בונה את סיכום 3 השורות לפי אותו מפרט של עמוד העגלה:
         יש הנחה →  סך N פריטים (מחיר מלא מחוק) / סך הנחה / סך לתשלום
         אין הנחה →  סך לתשלום בלבד
   • המחירים עוברים דרך אותה פונקציית fmt של עמוד העגלה, ולכן גם הדיוק זהה:
     התמה מציגה 229650 אגורות כ-"2,297 ₪" (עיגול), וכאן זה "2,296.5 ₪".

   ── שיקולי מימוש ──
   • שורת "סך לתשלום" היא ה-<label> + .hc__total__price *המקוריים* של התמה,
     ולא אלמנטים חדשים. שתי שורות המשנה נדחפות לפניהן. כך אם התמה תעדכן את
     המחיר בעצמה, היא עדיין מוצאת את האלמנט שהיא מכירה.
   • הפריסה נשענת על flex-wrap ולא על עטיפת האלמנטים המקוריים ב-<div> חדש,
     מאותה סיבה — לא לשנות את יחסי ההורה/ילד שהתמה מסתמכת עליהם.
   • הפונקציות שכותבות טקסט (setText) בודקות לפני הכתיבה אם הערך כבר נכון.
     זה מה שמונע לולאה אינסופית מול ה-MutationObserver: מעבר שני לא מייצר
     מוטציות, ולכן ה-observer לא נורה שוב. עדיף על flag/timeout כי אין חלון
     זמן שבו שינוי אמיתי של העגלה עלול "ליפול".
   • התו RLM (U+200F) נכתב כ-String.fromCharCode(0x200F) ולא כתו ממשי בקובץ.
     תו בלתי-נראה בקובץ שנדחף במלואו דרך ה-API הוא מקור ידוע לסטיית checksum
     בין המקומי לחי (קרה כאן פעמיים). ככה הקובץ נשאר ASCII מלבד העברית.
   • קובץ נפרד ולא הרחבה של pg-cart-extras.js: אותו קובץ נטען רק בעמוד העגלה
     (template.name == 'cart'), וה-drawer מופיע דווקא בכל שאר העמודים.
   ============================================================================ */
(function () {
  'use strict';

  var HOST_ID = 'bt-sidebar-cart';

  function host() {
    return document.getElementById(HOST_ID);
  }

  /* ---------------- פורמט כסף — זהה לעמוד העגלה ---------------- */
  var RLM = String.fromCharCode(0x200F);

  function money(cents) {
    return (cents / 100).toLocaleString('he-IL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  }

  function fmt(cents) {
    var neg = cents < 0;
    var n = money(Math.abs(cents));
    return neg ? (RLM + n + '-' + RLM + ' ₪') : (RLM + n + RLM + ' ₪');
  }

  /* ---------------- CSS ---------------- */
  function injectStyles() {
    if (document.getElementById('pg-cart-drawer-style')) return;
    var s = document.createElement('style');
    s.id = 'pg-cart-drawer-style';
    s.textContent =
      /* --- בורר הכמות: אותה פריסה כמו .qty-box--cart בעמוד העגלה --- */
      /* flex-wrap:nowrap הכרחי — המחלקה .flex של התמה מגדירה flex-wrap:wrap,
         ובלעדיו שלושת הילדים נשברו כל אחד לשורה משלו (נמדד: מינוס/מספר/פלוס
         ב-y 284/312/340) והמחיר נדרס עליהם. */
      '#bt-sidebar-cart .qty-box{display:flex !important;align-items:center !important;' +
      'justify-content:space-between !important;gap:2px !important;flex-wrap:nowrap !important;' +
      'width:90px !important;flex:0 0 90px !important;position:relative !important;}' +
      '#bt-sidebar-cart .qty-box .qty-box__ctrl{position:static !important;top:auto !important;' +
      'left:auto !important;right:auto !important;bottom:auto !important;' +
      'width:26px !important;height:26px !important;flex:0 0 auto !important;' +
      'border:none !important;background:transparent !important;box-shadow:none !important;}' +
      /* flex:1 1 0 + width:0 ולא flex:1 1 auto — ל-<input type=number> יש רוחב
         תוכן מובנה (ברירת מחדל size=20), ועם basis:auto הוא תפס את כל 90px
         ודחף את שני הכפתורים לשורות נפרדות. basis אפס מבטל את הרוחב המובנה
         ומשאיר לו בדיוק את מה שנשאר אחרי שני הכפתורים. */
      '#bt-sidebar-cart .qty-box .qty-box__input{position:static !important;width:0 !important;' +
      'height:26px !important;flex:1 1 0 !important;min-width:0 !important;' +
      'text-align:center !important;}' +
      /* --- המחיר: משקל רגיל + הסתרת ה-"x", כמו בעמוד העגלה --- */
      /* הערה חשובה (נמדד 31.7.2026): *לא* מעלים את המחיר לאותה שורה עם הבורר,
         למרות שכך זה נראה בעמוד העגלה. ה-drawer רחב 340px בלבד, ומתוכם
         90px לתמונה ו-63px לכפתור ההסרה — נשארים ~187px לעמודת הטקסט.
         ניסיון לשים בורר (90px) ומחיר זה לצד זה דחף את עמודת ההסרה אל מחוץ
         ל-drawer (נמדד left:-29px) ומחץ את המחיר ל-23px. בעמוד העגלה השורה
         רחבה בהרבה ולכן שם זה עובד. כאן המחיר נשאר בשורה נפרדת מתחת לבורר. */
      '#bt-sidebar-cart .hc__item__price{flex-wrap:wrap !important;font-weight:300 !important;}' +
      '#bt-sidebar-cart .hc__item__x{display:none !important;}' +
      /* --- התאמת רוחב הטבלה למכל של ה-drawer --- */
      /* נמצא אגב העבודה (31.7.2026, קדם לשינוי הזה): טבלת הפריטים רחבה
         313px בתוך מכל של 268px — 45px גלישה אופקית, שהתבטאה בסרגל גלילה
         אופקי חום ובולט בתחתית רשימת הפריטים, ובאייקון הפח שנחתך.
         table-layout:fixed + width:100% מכריחים את שלוש העמודות להיכנס.
         58px לעמודת ההסרה אינם שרירותיים: הקישור "עריכה בעגלה" שמוצג בשורות
         בנדל רחב 53px עם white-space:nowrap, ועמודה צרה ממנו החזירה את הגלישה
         (נבדק ב-34px — גלש 21px). התמונה יורדת מ-90px ל-70px כדי לפנות רוחב
         לעמודת הטקסט; בעמוד העגלה היא נשארת 90px, שם השורה רחבה בהרבה. */
      '#bt-sidebar-cart .hc__items table{width:100% !important;table-layout:fixed !important;}' +
      '#bt-sidebar-cart .hc__item__image{width:70px !important;}' +
      '#bt-sidebar-cart .hc__item__remove-wrap{width:58px !important;}' +
      /* --- סיכום 3 שורות --- */
      '#bt-sidebar-cart .hc__total.pg-hc-sum{display:flex !important;flex-wrap:wrap !important;' +
      'align-items:baseline !important;justify-content:space-between !important;}' +
      '#bt-sidebar-cart .hc__total.pg-hc-sum .pg-hc-row{width:100%;display:flex;' +
      'align-items:baseline;justify-content:space-between;gap:12px;margin-bottom:4px;}' +
      '#bt-sidebar-cart .hc__total.pg-hc-sum .pg-hc-label{font-size:14px;font-weight:300;color:#8a7d74;}' +
      '#bt-sidebar-cart .hc__total.pg-hc-sum .pg-hc-value{font-size:14px;font-weight:300;' +
      'color:#8a7d74;direction:rtl;}' +
      '#bt-sidebar-cart .hc__total.pg-hc-sum .pg-hc-row--items .pg-hc-value s{' +
      'text-decoration:line-through;opacity:.85;}' +
      '#bt-sidebar-cart .hc__total.pg-hc-sum .pg-hc-row--disc .pg-hc-label,' +
      '#bt-sidebar-cart .hc__total.pg-hc-sum .pg-hc-row--disc .pg-hc-value{color:#8a6f5e;}' +
      /* הקו המפריד יושב על תחתית שורת ההנחה — כך אין צורך באלמנט מפריד נוסף,
         והוא מופיע רק כשיש פירוט (השורה עצמה קיימת רק אז). */
      '#bt-sidebar-cart .hc__total.pg-hc-sum .pg-hc-row--disc{padding-bottom:8px;' +
      'margin-bottom:8px;border-bottom:1px solid rgba(87,75,68,.15);}' +
      '#bt-sidebar-cart .hc__total.pg-hc-sum.pg-hc-has-discount > label,' +
      '#bt-sidebar-cart .hc__total.pg-hc-sum.pg-hc-has-discount > .hc__total__price{' +
      'font-weight:600 !important;}';
    document.head.appendChild(s);
  }

  /* ---------------- עזרי DOM ---------------- */

  /* כותב רק אם הערך השתנה — זה מה שמונע לולאה מול ה-MutationObserver. */
  function setText(el, txt) {
    if (el && el.textContent !== txt) el.textContent = txt;
  }

  function mkRow(cls) {
    var d = document.createElement('div');
    d.className = 'pg-hc-row ' + cls;
    var l = document.createElement('span');
    l.className = 'pg-hc-label';
    var v = document.createElement('span');
    v.className = 'pg-hc-value';
    d.appendChild(l);
    d.appendChild(v);
    return d;
  }

  function drop(el) {
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  /* ---------------- הסיכום ---------------- */
  /*
     המבנה המקורי של התמה:
        <div class="hc__total flex">
          <label>סיכום</label>
          <div class="hc__total__price">2,297 ₪</div>
        </div>

     המבנה אחרי הטיפול (כשיש הנחה):
        <div class="hc__total flex pg-hc-sum pg-hc-has-discount">
          <div class="pg-hc-row pg-hc-row--items">סך 4 פריטים | 2,696 ₪ (מחוק)</div>
          <div class="pg-hc-row pg-hc-row--disc"> סך הנחה     | -399.5 ₪   </div>
          <label>סך לתשלום</label>
          <div class="hc__total__price">2,296.5 ₪</div>
        </div>
     כשאין הנחה שתי השורות הראשונות מוסרות, ונשארת רק "סך לתשלום".
  */
  function renderSummary(cart) {
    var h = host();
    if (!h) return;
    var tot = h.querySelector('.hc__total');
    if (!tot) return;
    var label = tot.querySelector('label');
    var priceEl = tot.querySelector('.hc__total__price');
    if (!label || !priceEl) return;

    if (!tot.classList.contains('pg-hc-sum')) tot.classList.add('pg-hc-sum');
    setText(label, 'סך לתשלום');
    setText(priceEl, fmt(cart.total_price));

    var discount = Math.abs(cart.total_discount || 0);
    var itemsRow = tot.querySelector('.pg-hc-row--items');
    var discRow = tot.querySelector('.pg-hc-row--disc');

    if (discount <= 0) {
      drop(itemsRow);
      drop(discRow);
      tot.classList.remove('pg-hc-has-discount');
      return;
    }

    if (!itemsRow) {
      itemsRow = mkRow('pg-hc-row--items');
      tot.insertBefore(itemsRow, label);
    }
    if (!discRow) {
      discRow = mkRow('pg-hc-row--disc');
      tot.insertBefore(discRow, label);
    }

    var n = cart.item_count || 0;
    setText(itemsRow.querySelector('.pg-hc-label'),
      'סך ' + n + ' ' + (n === 1 ? 'פריט' : 'פריטים'));

    /* המחיר המלא (לפני ההנחה) חייב להיות original_total_price ולא
       items_subtotal_price — האחרון כבר מנוכה מהנחות ברמת השורה, ואז הקו
       המחוק היה מציג את אותו סכום כמו "לתשלום". */
    var valueEl = itemsRow.querySelector('.pg-hc-value');
    var full = fmt(cart.original_total_price);
    var existing = valueEl.querySelector('s');
    if (!existing) {
      valueEl.textContent = '';
      existing = document.createElement('s');
      valueEl.appendChild(existing);
    }
    setText(existing, full);

    setText(discRow.querySelector('.pg-hc-label'), 'סך הנחה');
    setText(discRow.querySelector('.pg-hc-value'), fmt(-discount));

    if (!tot.classList.contains('pg-hc-has-discount')) tot.classList.add('pg-hc-has-discount');
  }

  /* ---------------- טעינה וסנכרון ---------------- */
  var timer = null;

  function refresh() {
    if (!host()) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(renderSummary)
      .catch(function () { /* שקט — אם ה-fetch נכשל, נשאר המבנה של התמה */ });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 120);
  }

  function boot() {
    var h = host();
    /* ה-drawer מרונדר בצד שרת, אבל בעמודים כבדים הוא לפעמים מגיע אחרי
       DOMContentLoaded. כמה ניסיונות חוזרים במקום להיכשל בשקט. */
    if (!h) {
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (host()) { clearInterval(iv); boot(); return; }
        if (tries >= 6) clearInterval(iv);
      }, 500);
      return;
    }
    injectStyles();
    refresh();

    /* התמה מרנדרת מחדש את כל תוכן ה-drawer בכל שינוי כמות/הסרה (AJAX),
       ואז השורות שלנו נמחקות. ה-observer בונה אותן מחדש. */
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(h, { childList: true, subtree: true });
    }

    /* פתיחת ה-drawer עצמה — לפעמים התוכן נטען רק אז. */
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest
        ? e.target.closest('[data-target="#bt-sidebar-cart"]')
        : null;
      if (t) schedule();
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
